import {all, call, takeLatest, takeLeading, put} from 'redux-saga/effects';
import {ActionType, actions} from './action';
import {
  getHasNew,
  getNoticeList,
  getUserCashInfo,
  getWebToAppBanner,
} from './apiGet';
import {getUserId} from '../../auth/state/selector';
import {callApiContentLike} from '../../commonSub/state/callApi';
import {BILLING_PATH, REGION} from '../constant';
import {Page} from '../constant/page';
import {selectT} from '../i18n';
import callApi, {I_ApiResult} from '../util/api';
import {getTimeStamp} from '../util/date';
import {IPagingParam, makeFetchSaga} from '../util/fetch';
import {typedSelect} from '../util/saga';

function* fetchLastUpdatedTime() {
  const url = '/composite/v1/last-updated-at';
  const {
    success,
    data,
  }: I_ApiResult<{
    giftbox?: string;
    notification?: string;
    notice?: string;
  }> = yield call(callApi, {url, params: {platform: 'web'}});
  if (success && data) {
    const lastUpdateTime = {
      giftbox: data.giftbox ? getTimeStamp(data.giftbox) : 0,
      notification: data.notification ? getTimeStamp(data.notification) : 0,
      notice: data.notice ? getTimeStamp(data.notice) : 0,
    };

    const hasNewInfo = {
      [Page.Home]: getHasNew({
        page: Page.Home,
        lastUpdateTime: lastUpdateTime.notice,
      }),
    };

    yield put(actions.setPropertyValues('hasNewInfo', hasNewInfo));
  }
}

function* fetchUserCashInfo() {
  const userId = yield* typedSelect(getUserId);

  const {success, data} = yield call(callApi, {
    url: `billing/v1/accounts/details/${BILLING_PATH[REGION]}/${userId}`,
    params: {
      userId,
    },
  });

  if (success) {
    const {totalCash, ...userCashInfo} = getUserCashInfo(data);
    yield put(actions.setUserCashInfo(userCashInfo, totalCash));
  }
}

function* fetchNoticeList(
  action: ReturnType<typeof actions.fetchNoticeList>,
  params: IPagingParam
) {
  const t = yield* selectT();
  const {id, type} = action.payload;

  let url = '/notification/v1/';

  if (type === 'content') {
    url = url + `content-notice/${id}`;
  } else if (type === 'notification') {
    url = url + 'notice';
  } else {
    // TODO: faq 국내는 카카오 API 연동해야된다. 일단 글로벌로 연동
    url = url + 'faq';
  }

  const {success, data} = yield call(callApi, {
    url,
    params,
  });

  if (success) {
    const noticeList = getNoticeList(t, data);
    yield put(actions.addNoticeList(noticeList, action.isFirst));
  }
}

function* fetchSetContentLike(
  action: ReturnType<typeof actions.fetchSetContentLike>
) {
  const {id, isLike} = action.payload;
  yield put(actions.setContentLike({id, isLike}));
  const {success} = yield* callApiContentLike({id, isOn: isLike});
  if (!success) {
    yield put(actions.setContentLike({id, isLike: !isLike}));
  }
}

function* fetchWebToAppBanner(
  action: ReturnType<typeof actions.fetchWebToAppBanner>
) {
  const {isViewerPage} = action.payload;
  const {success, data} = yield call(callApi, {
    url: isViewerPage
      ? '/section/v1/view/pin-banners/viewer'
      : '/section/v1/view/pin-banners/general',
  });

  if (success && data) {
    const banner = getWebToAppBanner(data);
    if (banner) {
      yield put(actions.setWebToAppBanner(banner, isViewerPage));
    }
  }
}

export default function* saga() {
  yield all([
    takeLatest(
      ActionType.FetchLastUpdatedTime,
      makeFetchSaga({fetchSaga: fetchLastUpdatedTime, canCache: true})
    ),
    takeLeading(
      ActionType.FetchUserCashInfo,
      makeFetchSaga({fetchSaga: fetchUserCashInfo, canCache: false})
    ),
    takeLeading(
      ActionType.FetchNoticeList,
      makeFetchSaga({fetchSaga: fetchNoticeList, canCache: true})
    ),
    takeLeading(
      ActionType.FetchSetContentLike,
      makeFetchSaga({fetchSaga: fetchSetContentLike, canCache: false})
    ),
    takeLeading(
      ActionType.FetchWebToAppBanner,
      makeFetchSaga({fetchSaga: fetchWebToAppBanner, canCache: true})
    ),
  ]);
}
